import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import NewJobDialog from "@/components/NewJobDialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Clock, CheckCircle2, MapPin, User, Calendar, FolderOpen, Plus, Search, Pencil,
} from "lucide-react";
import { toast } from "sonner";

function KpiCard({ label, value, color, testId }) {
  return (
    <Card className="p-6 sm:p-7 bg-white border-slate-200 shadow-sm rounded-xl" data-testid={testId}>
      <div className={`kpi-number ${color}`}>{value}</div>
      <div className="text-sm text-slate-500 mt-3 text-center">{label}</div>
    </Card>
  );
}

function StatusTab({ active, onClick, children, icon: Icon, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={
        "flex-1 h-14 rounded-xl flex items-center justify-center gap-2 font-semibold text-sm sm:text-base transition border " +
        (active
          ? "brand-navy text-white border-transparent shadow-sm"
          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300")
      }
    >
      <Icon className="w-4 h-4" />
      {children}
    </button>
  );
}

function JobRow({ job, onClick, onEdit, isAdmin }) {
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
  return (
    <Card
      onClick={onClick}
      className="job-card cursor-pointer p-5 sm:p-6 border-slate-200 rounded-xl bg-white"
      data-testid={`job-row-${job.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-lg sm:text-xl font-bold text-brand-navy truncate">{job.project_name}</h3>
          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-600">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-rose-500" />
              <span className="truncate max-w-[240px]">{job.site_address}</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <User className="w-4 h-4 text-slate-400" />
              {job.manager}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-slate-400" />
              {fmtDate(job.start_date)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <FolderOpen className="w-4 h-4 text-slate-400" />
              {job.files_count} files
            </span>
            <span className="inline-flex items-center gap-1.5 font-semibold text-brand-orange">
              {job.total_hours}h totali · {job.total_days} gg
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isAdmin && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit?.(job); }}
              className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
              aria-label="Modifica"
              data-testid={`job-edit-button-${job.id}`}
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
          <span
            className={
              "text-xs font-semibold px-3 py-1 rounded-full " +
              (job.status === "active"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-slate-100 text-slate-600")
            }
          >
            {job.status === "active" ? "Attivo" : "Concluso"}
          </span>
        </div>
      </div>
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const [stats, setStats] = useState({ total: 0, active: 0, completed: 0 });
  const [jobs, setJobs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active");
  const [query, setQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = async () => {
    const { data } = await api.get("/jobs/stats");
    setStats(data);
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("status", statusFilter);
      if (query.trim()) params.set("q", query.trim());
      const { data } = await api.get(`/jobs?${params.toString()}`);
      setJobs(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => {
    const t = setTimeout(loadJobs, 200);
    return () => clearTimeout(t);
  }, [statusFilter, query]);

  const handleCreated = () => { loadStats(); loadJobs(); toast.success("Lavoro salvato"); };

  const openNew = () => { setEditingJob(null); setDialogOpen(true); };
  const openEdit = (job) => { setEditingJob(job); setDialogOpen(true); };

  const emptyMsg = useMemo(() => {
    if (loading) return "Caricamento...";
    if (query) return `Nessun lavoro trovato per "${query}".`;
    return statusFilter === "active" ? "Nessun lavoro attivo." : "Nessun lavoro concluso.";
  }, [loading, query, statusFilter]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* KPI */}
        <div className="grid grid-cols-3 gap-3 sm:gap-5">
          <KpiCard label="Totale" value={stats.total} color="text-brand-navy" testId="kpi-total" />
          <KpiCard label="Attivi" value={stats.active} color="text-emerald-600" testId="kpi-active" />
          <KpiCard label="Conclusi" value={stats.completed} color="text-sky-600" testId="kpi-completed" />
        </div>

        {/* Tabs */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4">
          <StatusTab
            active={statusFilter === "active"}
            onClick={() => setStatusFilter("active")}
            icon={Clock}
            testId="tab-active-jobs"
          >
            Lavori Attivi
          </StatusTab>
          <StatusTab
            active={statusFilter === "completed"}
            onClick={() => setStatusFilter("completed")}
            icon={CheckCircle2}
            testId="tab-completed-jobs"
          >
            Lavori Conclusi
          </StatusTab>
        </div>

        {/* Search + New */}
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca progetto, cliente, indirizzo..."
              className="h-14 pl-11 bg-white rounded-xl border-slate-200"
              data-testid="search-input"
            />
          </div>
          {isAdmin && (
            <Button
              onClick={openNew}
              className="h-14 px-6 brand-navy text-white hover:opacity-90 rounded-xl font-semibold"
              data-testid="new-job-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo lavoro
            </Button>
          )}
        </div>

        {/* Jobs */}
        <div className="mt-6 space-y-3" data-testid="jobs-list">
          {jobs.length === 0 && (
            <div className="p-10 text-center text-slate-500 bg-white rounded-xl border border-dashed border-slate-300" data-testid="jobs-empty">
              {emptyMsg}
            </div>
          )}
          {jobs.map((j) => (
            <JobRow
              key={j.id}
              job={j}
              isAdmin={isAdmin}
              onClick={() => navigate(`/jobs/${j.id}`)}
              onEdit={openEdit}
            />
          ))}
        </div>
      </main>

      <NewJobDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={handleCreated}
        initial={editingJob}
      />
    </div>
  );
}
